"use client"

import { useState } from "react"
import { useRouter, usePathname, useSearchParams } from "next/navigation"
import FilterBar from "@modules/store/components/filter-bar"

/**
 * URL params owned by the filter bar:
 *   - sortBy=<created_at|price_asc|price_desc>
 *   - manufacturer=<comma-separated>
 *   - inStock=1
 *   - sale=1
 *   - page=<n>     (cleared on any filter change so user is not stranded
 *                  past the new last page)
 */
export default function FilterBarWrapper({
  sortBy,
  manufacturers,
  selectedManufacturers,
  stockOnly,
  saleOnly,
}: {
  sortBy: string
  manufacturers: string[]
  selectedManufacturers: string[]
  stockOnly: boolean
  saleOnly: boolean
}) {
  const [view, setView] = useState<"table" | "grid">("table")
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const buildParams = () =>
    new URLSearchParams(searchParams.toString())

  const push = (params: URLSearchParams) => {
    params.delete("page")
    const qs = params.toString()
    router.push(qs ? `${pathname}?${qs}` : pathname)
  }

  const onSortChange = (val: string) => {
    const params = buildParams()
    params.set("sortBy", val)
    push(params)
  }

  const onToggleManufacturer = (val: string) => {
    const params = buildParams()
    const current = (params.get("manufacturer") || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
    const next = current.includes(val)
      ? current.filter((v) => v !== val)
      : [...current, val]
    if (next.length) {
      params.set("manufacturer", next.join(","))
    } else {
      params.delete("manufacturer")
    }
    push(params)
  }

  const onTabChange = (tab: "all" | "stock" | "sale") => {
    const params = buildParams()
    params.delete("inStock")
    params.delete("sale")
    if (tab === "stock") params.set("inStock", "1")
    if (tab === "sale") params.set("sale", "1")
    push(params)
  }

  const onClearAll = () => {
    const params = buildParams()
    params.delete("manufacturer")
    params.delete("inStock")
    params.delete("sale")
    push(params)
  }

  return (
    <FilterBar
      sortBy={sortBy}
      onSortChange={onSortChange}
      view={view}
      onViewChange={setView}
      manufacturers={manufacturers}
      selectedManufacturers={selectedManufacturers}
      onToggleManufacturer={onToggleManufacturer}
      stockOnly={stockOnly}
      saleOnly={saleOnly}
      onTabChange={onTabChange}
      onClearAll={onClearAll}
    />
  )
}
